/**
 * Fast location utilities - Optimized location handling for modern URLs
 * Includes city detection for address validation and user address selection
 */

import { CITIES } from '@/store';

export interface CityInfo {
  slug: string;
  value: string;
  id: string; // alias kept for backward compat — same as value
  label: string;
}

const CITY_ZONES = [
  {
    label: 'Hà Nội',
    value: 'HAN',
    slug: 'ha-noi',
    lat: 21.0285,
    lng: 105.8542,
    radiusKm: 30,
  },
  {
    label: 'Đà Nẵng',
    value: 'DAD',
    slug: 'da-nang',
    lat: 16.0678,
    lng: 108.2208,
    radiusKm: 20,
  },
  {
    label: 'Hồ Chí Minh',
    value: 'SGN',
    slug: 'ho-chi-minh',
    lat: 10.8231,
    lng: 106.6297,
    radiusKm: 30,
  },
  {
    label: 'Tam Kỳ',
    value: 'VCL',
    slug: 'tam-ky',
    lat: 15.5736,
    lng: 108.474,
    radiusKm: 10,
  },
] as const;

function haversineKm(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Extract lat/lng from URL search params
 * @param searchParams - URL search parameters
 * @returns Object with lat, lng, and hasLocation flag
 */
export function extractLocationFromUrlParams(searchParams: {
  lat?: string | string[];
  lng?: string | string[];
}): {
  lat?: number;
  lng?: number;
  hasLocation: boolean;
} {
  const latParam = Array.isArray(searchParams.lat)
    ? searchParams.lat[0]
    : searchParams.lat;
  const lngParam = Array.isArray(searchParams.lng)
    ? searchParams.lng[0]
    : searchParams.lng;

  if (latParam && lngParam) {
    const lat = parseFloat(latParam);
    const lng = parseFloat(lngParam);

    if (!isNaN(lat) && !isNaN(lng)) {
      return { lat, lng, hasLocation: true };
    }
  }

  return { hasLocation: false };
}

/**
 * Create location params object for API calls
 * @param lat Latitude
 * @param lng Longitude
 * @param defaultRadius Default radius if not provided
 * @returns Object with lat, lng, and radius for API calls
 */
export function createLocationParams(
  lat?: number,
  lng?: number,
  defaultRadius = 10000
): { lat?: number; lng?: number; radius?: number } {
  if (lat && lng) {
    return { lat, lng, radius: defaultRadius };
  }
  return {};
}

/**
 * Generate place_id from coordinates (for backward compatibility)
 * @param lat Latitude
 * @param lng Longitude
 * @returns Place ID string
 */
export function generatePlaceId(lat: number, lng: number): string {
  return `${lat}_${lng}`;
}

/**
 * Detects city using Haversine distance to city centers.
 * Returns the closest city whose center is within its radiusKm.
 * Falls back to SGN when coordinates are outside all city zones.
 */
export function detectCityFromCoordinates(lat: number, lng: number): CityInfo {
  let best: (typeof CITY_ZONES)[number] | undefined;
  let bestDist = Infinity;

  for (const zone of CITY_ZONES) {
    const dist = haversineKm(lat, lng, zone.lat, zone.lng);
    if (dist <= zone.radiusKm && dist < bestDist) {
      best = zone;
      bestDist = dist;
    }
  }

  const city = best ?? CITY_ZONES[2]; // fallback SGN
  return {
    slug: city.slug,
    value: city.value,
    id: city.value,
    label: city.label,
  };
}

/**
 * Find city info from CITIES array by slug
 * @param citySlug City slug to search for
 * @returns City from CITIES array or null
 */
export function findCityBySlug(citySlug: string) {
  return CITIES.find((city) => city.slug === citySlug);
}

/**
 * Detect city from address text (fallback method)
 * @param address Address string to search in
 * @returns Matched city from CITIES array or null
 */
export function detectCityFromAddressText(address?: string) {
  if (!address) return null;
  return CITIES.find((city) => address.includes(city.label));
}

/**
 * Calculate distance between two coordinates using Haversine formula
 * @param point1 First coordinate {lat, lng}
 * @param point2 Second coordinate {lat, lng}
 * @returns Distance in meters
 */
export function calculateDistance(
  point1: { lat: number; lng: number },
  point2: { lat: number; lng: number }
): number {
  const R = 6371000; // Earth radius in meters
  const dLat = ((point2.lat - point1.lat) * Math.PI) / 180;
  const dLng = ((point2.lng - point1.lng) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((point1.lat * Math.PI) / 180) *
      Math.cos((point2.lat * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Create address object from coordinates
 * @param lat Latitude
 * @param lng Longitude
 * @param address Optional address string
 * @returns Basic address object
 */
export function createBasicAddress(lat: number, lng: number, address?: string) {
  return {
    lat,
    lng,
    address: address ?? `${lat}, ${lng}`,
    place_id: generatePlaceId(lat, lng),
    second_label: address ?? 'Vị trí của bạn',
  };
}
